import React, { useContext, useEffect, useState } from "react";

import { Button, Divider, H5, Icon, MenuItem, Card } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";

import "components/Navbar/SecondaryNavbar.scss";

import { Popover2 } from "@blueprintjs/popover2";
import { capitalize } from "lodash";
import { useProjects } from "hooks/useProjects";
import { Project } from "types";
import { SelectedProjectContext, SettingsContext } from "utils/context";
import CustomLink from "routes/CustomLink";
import { useSearchParams } from "react-router-dom";
import { projectKey } from "utils/utils";
import { ParseToaster } from "utils/toasters";

const viewItems = ["Runs", "Schedules", "Actions"];

const ViewSelect = Select.ofType<string>();

const renderView: ItemRenderer<string> = (view, { handleClick, modifiers, index }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }

    return (
        <CustomLink key={index} page={"project"} view={view.toLowerCase()}>
            <MenuItem active={modifiers.active} onClick={handleClick} text={view} />
        </CustomLink>
    );
};

const ProjectSelect = Select.ofType<Project>();

const filterProject: ItemPredicate<Project> = (query, project) => {
    return project.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};

const renderProject: ItemRenderer<Project> = (project, { handleClick, modifiers, index }) => {
    if (!modifiers.matchesPredicate) {
        return null;
    }
    return (
        <MenuItem
            active={modifiers.active}
            key={`m-${index}`}
            onClick={handleClick}
            text={project.name}
            icon="document"
            labelElement={
                <Popover2 placement="right" interactionKind="hover" content={getPopoverContent(project)}>
                    <Icon icon="help" />
                </Popover2>
            }
        />
    );
};

function getPopoverContent(project: Project) {
    return (
        <Card>
            <H5 style={{ color: "#48aff0" }}>{project.name}</H5>
            <p>Company: {project.companyName}</p>
            {project.description && <p>Description: {project.description}</p>}
        </Card>
    );
}

type Props = {
    page: string;
    actions?: Array<JSX.Element>;
    actionsCenter?: Array<JSX.Element>;
};

export default function ProjectMenu({ page, actions, actionsCenter }: Props) {
    const [pageView, setPageView] = useState(capitalize(page));
    const [selectedProject, setSelectedProject] = useContext(SelectedProjectContext);
    const [settings] = useContext(SettingsContext);
    const [projects, { loading, error }] = useProjects(settings.showGlobal);
    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        if (searchParams) {
            const encoded = searchParams.get(projectKey);

            if (encoded) {
                try {
                    const project = atob(encoded);
                    setSelectedProject(projects.find((p) => project === p.projectId));
                } catch (e) {
                    ParseToaster.show({
                        message: `Unexprected error encoding URL.`,
                        intent: "danger"
                    });
                }
            }
        }
    }, [projects, searchParams, setSelectedProject]);

    return (
        <div className="secondary-navbar">
            <div className="left-group">
                <ProjectSelect
                    itemPredicate={filterProject}
                    itemRenderer={renderProject}
                    items={projects ? projects : []}
                    activeItem={selectedProject}
                    onItemSelect={(p) => {
                        setSelectedProject(p);
                        setSearchParams({
                            [projectKey]: btoa(p.projectId)
                        });
                    }}
                    disabled={!!error}
                    noResults={<MenuItem disabled={true} text="No results." />}
                    popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                >
                    <Button
                        key="project-btn"
                        icon="projects"
                        rightIcon="caret-down"
                        text={selectedProject ? `${selectedProject.name}` : "Select a Project"}
                        loading={loading}
                    />
                </ProjectSelect>
                <Divider />
                <ViewSelect
                    disabled={!selectedProject}
                    filterable={false}
                    itemRenderer={renderView}
                    items={viewItems}
                    activeItem={pageView}
                    onItemSelect={(v) => {
                        setPageView(v);
                        // history.push(
                        //     `/projects/${selectedProject ? selectedProject.projectId + "/" : ""}${v.toLowerCase()}`
                        // );
                    }}
                    popoverProps={{ minimal: true }}
                >
                    <Button disabled={!selectedProject} icon="eye-open" rightIcon="caret-down" text={pageView} />
                </ViewSelect>
                {actionsCenter && <Divider />}
            </div>
            {actionsCenter && selectedProject && <div className="center-group">{actionsCenter}</div>}
            {actionsCenter && <Divider />}
            {actions && selectedProject && <div className="right-group">{actions}</div>}
        </div>
    );
}
